import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { ALGO_DEVICE_SUPPORT_HELP, ALGO_DEVICE_SUPPORT_LABEL } from './algoDeviceSupport';
import {
  getTenantsAdminList,
  listTenantSupportAccess,
  revokeTenantSupportAccess,
  saveTenantSupportAccess,
} from '../../api/backendApi';

const LEVELS = [
  { value: 'view', label: 'View only' },
  { value: 'manage', label: 'Manage settings' },
];

function requestId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `support-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function tenantEligible(tenant) {
  if (!tenant) return false;
  const id = String(tenant.tenantId || tenant.id || '').trim();
  if (!id || ['global', 'system', 'unallocated', 'default'].includes(id)) return false;
  if (tenant.tombstone === true || tenant.deletionLock === true) return false;
  if (String(tenant.deletionStatus || '').toLowerCase() === 'deleted') return false;
  if (tenant.active === false) return false;
  const status = String(tenant.status || '').toLowerCase();
  return !['deleted', 'disabled', 'archived', 'inactive'].includes(status);
}

function tenantLabel(tenant) {
  return String(tenant?.name || tenant?.displayName || tenant?.tenantId || tenant?.id || '').trim();
}

export default function TenantSupportAccessTab({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [accessLevel, setAccessLevel] = useState('view');
  const [algoDeviceSupport, setAlgoDeviceSupport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const person = [user?.displayName, user?.name, user?.email].find(Boolean) || 'This user';

  const load = () => {
    setLoading(true);
    setError('');
    return Promise.all([
      listTenantSupportAccess(user.id),
      getTenantsAdminList(),
    ]).then(([assignmentResponse, tenantRows]) => {
      const rows = Array.isArray(assignmentResponse?.data) ? assignmentResponse.data : [];
      setAssignments(rows.filter((row) => row.status !== 'revoked'));
      setTenants((tenantRows || []).filter(tenantEligible));
    }).catch((err) => {
      setError(err?.message || 'Could not load tenant support access');
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [user?.id]);

  const choices = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tenants.filter((tenant) => {
      const id = String(tenant.tenantId || tenant.id || '');
      const label = tenantLabel(tenant).toLowerCase();
      return !term || label.includes(term) || id.toLowerCase().includes(term);
    }).slice(0, 20);
  }, [tenants, search]);

  const save = async () => {
    if (!tenantId || !accessLevel) return;
    const current = assignments.find((row) => row.tenantId === tenantId);
    setSaving(true);
    setError('');
    try {
      await saveTenantSupportAccess(user.id, tenantId, {
        accessLevel,
        algoDeviceSupport: algoDeviceSupport === true,
        expectedVersion: current?.version || 0,
        requestId: requestId(),
      });
      setTenantId('');
      setAlgoDeviceSupport(false);
      await load();
    } catch (err) {
      setError(err?.message || 'Could not save tenant support access');
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (row) => {
    setSaving(true);
    setError('');
    try {
      await revokeTenantSupportAccess(user.id, row.tenantId, {
        expectedVersion: row.version || 0,
        requestId: requestId(),
      });
      await load();
    } catch (err) {
      setError(err?.message || 'Could not revoke tenant support access');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Tenant support for {person}{user?.email ? ` (${user.email})` : ''}. They keep their own login and their normal tenant role. This does not add a dealer relationship, a tenant membership, or Superadmin access.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Separate platform permissions, such as Tenant management or Platform support, can still give this person wider access. Tenant support access does not change those grants.
      </Typography>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? <Typography>Loading tenant support access…</Typography> : null}
      {!loading && assignments.length === 0 ? (
        <Typography sx={{ mb: 2 }}>No tenant support assignments.</Typography>
      ) : null}
      {!loading && assignments.map((row) => (
        <Box key={row.tenantId} sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography>
            {row.tenantName || row.tenantId}
            {' · '}
            {row.accessLevel === 'manage' ? 'Manage settings' : 'View only'}
            {row.algoDeviceSupport === true ? ' · Algo device support' : ''}
          </Typography>
          <Button size="small" onClick={() => revoke(row)} disabled={saving}>Revoke</Button>
        </Box>
      ))}
      <TextField
        label="Find a tenant"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        size="small"
        fullWidth
        sx={{ mt: 1, mb: 2 }}
      />
      <TextField
        select
        label="Tenant"
        value={tenantId}
        onChange={(event) => setTenantId(event.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        {choices.length === 0 ? <MenuItem value="" disabled>No eligible tenants</MenuItem> : null}
        {choices.map((tenant) => {
          const id = String(tenant.tenantId || tenant.id);
          return <MenuItem key={id} value={id}>{tenantLabel(tenant)}</MenuItem>;
        })}
      </TextField>
      <TextField
        select
        label="Access"
        value={accessLevel}
        onChange={(event) => setAccessLevel(event.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
      >
        {LEVELS.map((level) => (
          <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
        ))}
      </TextField>
      <FormControlLabel
        control={<Checkbox checked={algoDeviceSupport === true} onChange={(event) => setAlgoDeviceSupport(event.target.checked)} />}
        label={ALGO_DEVICE_SUPPORT_LABEL}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{ALGO_DEVICE_SUPPORT_HELP}</Typography>
      <Button variant="contained" onClick={save} disabled={saving || !tenantId}>
        {saving ? 'Saving…' : 'Save assignment'}
      </Button>
    </Box>
  );
}
